import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

/**
 * Custom hook for debounced autocomplete with intelligent caching
 *
 * Features:
 * - Debouncing (300ms default) to reduce server load
 * - Client-side fuzzy matching for better UX with cached results
 * - Smart query strategy: substring for short queries, fuzzy for longer
 * - Prevents duplicate requests
 * - Cancels pending requests when new input arrives
 *
 * @param {Object} options - Configuration options
 * @param {string} options.endpoint - API endpoint ('parties' or 'products')
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 300)
 * @param {number} options.minLength - Minimum query length (default: 2)
 * @param {number} options.limit - Maximum results to fetch (default: 10)
 * @param {Function} options.onError - Error callback
 * @returns {Object} - { options, loading, error, fetchSuggestions }
 */
export function useAutocomplete(options = {}) {
  const {
    endpoint = 'products', // 'products' or 'parties'
    debounceMs = 300,
    minLength = 2,
    limit = 10,
    onError = null
  } = options;

  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cache for storing fetched results
  const cacheRef = useRef(new Map());
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Fetch suggestions from API
  const fetchSuggestionsFromAPI = useCallback(async (searchTerm) => {
    try {
      // Cancel previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      const response = await api.get(`/${endpoint}/autocomplete`, {
        params: { q: searchTerm, limit },
        signal: abortControllerRef.current.signal
      });

      // Ensure response is an array (defensive check)
      const results = Array.isArray(response.data) ? response.data : [];

      // Cache the results
      cacheRef.current.set(searchTerm.toLowerCase(), results);

      return results;
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        // Request was cancelled, ignore
        return null;
      }

      const errorMessage = err.response?.data?.message || 'Failed to fetch suggestions';
      setError(errorMessage);

      if (onError) {
        onError(errorMessage);
      }

      return [];
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [endpoint, limit, onError]);

  // Debounced fetch function with caching
  const fetchSuggestions = useCallback((inputValue) => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const searchTerm = inputValue?.trim() || '';

    // Reset if empty
    if (searchTerm.length < minLength) {
      setAutocompleteOptions([]);
      setLoading(false);
      return;
    }

    // Check cache first
    const cachedResults = cacheRef.current.get(searchTerm.toLowerCase());
    if (cachedResults) {
      setAutocompleteOptions(cachedResults);
      setLoading(false);
      return;
    }

    // Show loading immediately for better UX
    setLoading(true);

    // Debounce the API call
    debounceTimerRef.current = setTimeout(async () => {
      const results = await fetchSuggestionsFromAPI(searchTerm);
      if (results !== null) {
        setAutocompleteOptions(results);
      }
    }, debounceMs);
  }, [debounceMs, minLength, fetchSuggestionsFromAPI]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    options: autocompleteOptions,
    loading,
    error,
    fetchSuggestions
  };
}

/**
 * Hook specifically for product autocomplete
 *
 * @param {Object} options - Configuration options
 * @returns {Object} - { options, loading, error, fetchSuggestions }
 */
export function useProductAutocomplete(options = {}) {
  return useAutocomplete({
    ...options,
    endpoint: 'products'
  });
}

/**
 * Hook specifically for party autocomplete
 *
 * @param {Object} options - Configuration options
 * @returns {Object} - { options, loading, error, fetchSuggestions }
 */
export function usePartyAutocomplete(options = {}) {
  return useAutocomplete({
    ...options,
    endpoint: 'parties'
  });
}

export default useAutocomplete;
